import { useEffect, useRef, useState } from "react";

/**
 * Premium custom cursor:
 * - 6px solid dot follows mouse instantly
 * - 32px ring lerps behind with spring easing
 * - Ring scales up & inverts when hovering interactive elements
 * - Disabled on touch / coarse-pointer devices
 * - Uses mix-blend-difference for self-adjusting contrast
 */
export const CustomCursor = () => {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const [enabled, setEnabled] = useState(false);
  const [hovering, setHovering] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    setEnabled(mq.matches);
    const onChange = (e) => setEnabled(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const target = { x: -100, y: -100 };
    const ring = { x: -100, y: -100 };
    let raf;

    const onMove = (e) => {
      target.x = e.clientX;
      target.y = e.clientY;
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${target.x - 3}px, ${target.y - 3}px, 0)`;
      }
    };

    const onOver = (e) => {
      const t = e.target;
      const interactive =
        t?.closest?.(
          'a, button, input, textarea, select, [role="button"], [data-cursor="hover"]'
        );
      setHovering(Boolean(interactive));
    };

    const tick = () => {
      ring.x += (target.x - ring.x) * 0.18;
      ring.y += (target.y - ring.y) * 0.18;
      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(${ring.x - 16}px, ${ring.y - 16}px, 0)`;
      }
      raf = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mouseover", onOver, { passive: true });
    raf = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseover", onOver);
      cancelAnimationFrame(raf);
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <>
      <div
        ref={dotRef}
        aria-hidden="true"
        className="fixed top-0 left-0 z-[9999] pointer-events-none mix-blend-difference will-change-transform"
        style={{ transform: "translate3d(-100px,-100px,0)" }}
      >
        <div className="h-1.5 w-1.5 rounded-full bg-white" />
      </div>
      <div
        ref={ringRef}
        aria-hidden="true"
        className="fixed top-0 left-0 z-[9998] pointer-events-none mix-blend-difference will-change-transform"
        style={{ transform: "translate3d(-100px,-100px,0)" }}
      >
        <div
          className={`rounded-full border border-white transition-all duration-300 ease-out ${
            hovering ? "h-12 w-12 -translate-x-2 -translate-y-2 bg-white/10" : "h-8 w-8"
          }`}
        />
      </div>
    </>
  );
};

export default CustomCursor;
